import { useMemo, useState } from 'react';
import { Modal, Tabs } from 'antd';
import ReactImageGallery from 'react-image-gallery';
import 'react-image-gallery/styles/css/image-gallery.css';
import { translate } from '../../../lib/lang.helper';
import { useSelector } from 'react-redux';
import { selectLang } from '../../../store/ui/ui.selector';

const readFileWall = (file: string) => {
  const ext = file.split('.').pop()?.toLowerCase();
  if (ext === 'heic' || ext === 'heif') {
    const res = `https://images.weserv.nl/?url=${encodeURIComponent(
      file,
    )}&output=jpg`;
    return res;
  }
  return file;
};

const { TabPane } = Tabs;

interface Props {
  visible: boolean;
  onClose: () => void;
  files: string[];
}

const isPdfFile = (url: string): boolean => {
  return url.toLowerCase().endsWith('.pdf');
};

const FilesViewModal = ({ visible, onClose, files }: Props) => {
  const lang = useSelector(selectLang);

  const [activeTab, setActiveTab] = useState<string>('images');

  const { images, pdfs } = useMemo(() => {
    const images: { original: string; thumbnail: string }[] = [];
    const pdfs: string[] = [];

    files.forEach(file => {
      if (isPdfFile(file)) {
        pdfs.push(file);
      } else {
        images.push({
          original: readFileWall(file),
          thumbnail: readFileWall(file),
        });
      }
    });

    return { images, pdfs };
  }, [files]);

  useMemo(() => {
    if (images.length > 0 && pdfs.length === 0) {
      setActiveTab('images');
    } else if (pdfs.length > 0 && images.length === 0) {
      setActiveTab('pdfs');
    }
  }, [images.length, pdfs.length]);

  return (
    <Modal
      centered
      visible={visible}
      onOk={onClose}
      onCancel={onClose}
      width={1400}
      bodyStyle={{ minHeight: '400px' }}
    >
      {images.length > 0 && pdfs.length > 0 ? (
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab={`Images (${images.length})`} key='images'>
            <ReactImageGallery items={images} />
          </TabPane>
          <TabPane tab={`PDFs (${pdfs.length})`} key='pdfs'>
            <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              {pdfs.map((pdf, index) => (
                <div key={index} style={{ marginBottom: '20px' }}>
                  <div
                    style={{
                      marginBottom: '10px',
                      fontWeight: 600,
                      fontSize: '14px',
                    }}
                  >
                    PDF {index + 1}
                  </div>
                  <iframe
                    src={pdf}
                    style={{
                      width: '100%',
                      height: '600px',
                      border: '1px solid #d9d9d9',
                      borderRadius: '4px',
                    }}
                    title={`PDF ${index + 1}`}
                  />
                </div>
              ))}
            </div>
          </TabPane>
        </Tabs>
      ) : images.length > 0 ? (
        <ReactImageGallery items={images} />
      ) : pdfs.length > 0 ? (
        <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {pdfs.map((pdf, index) => (
            <div key={index} style={{ marginBottom: '20px' }}>
              {pdfs.length > 1 && (
                <div
                  style={{
                    marginBottom: '10px',
                    fontWeight: 600,
                    fontSize: '14px',
                  }}
                >
                  PDF {index + 1}
                </div>
              )}
              <iframe
                src={pdf}
                style={{
                  width: '100%',
                  height: '600px',
                  border: '1px solid #d9d9d9',
                  borderRadius: '4px',
                }}
                title={`PDF ${index + 1}`}
              />
            </div>
          ))}
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          {translate(lang, 'No files to display')}
        </div>
      )}
    </Modal>
  );
};

export default FilesViewModal;
